/**
 * Mock Campaigns Data
 *
 * Sample campaigns for development and testing.
 * Includes various schedule configurations to test the scheduler.
 */

import { Campaign, PlaylistItem } from '~/services/scheduler/types';

// =============================================================================
// PLAYLIST ITEMS (reusing videos from existing mock)
// =============================================================================

const morningPlaylist: PlaylistItem[] = [
  {
    id: 'media-1',
    type: 'video',
    name: 'Big Buck Bunny',
    url: 'https://storage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
    duration: 596,
    fileSize: 158008374,
    checksum: 'abc123',
    mimeType: 'video/mp4',
  },
  {
    id: 'media-2',
    type: 'video',
    name: 'Elephant Dream',
    url: 'https://storage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4',
    duration: 653,
    fileSize: 64657027,
    checksum: 'def456',
    mimeType: 'video/mp4',
  },
];

const afternoonPlaylist: PlaylistItem[] = [
  {
    id: 'media-3',
    type: 'video',
    name: 'For Bigger Blazes',
    url: 'https://storage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4',
    duration: 15,
    fileSize: 2299653,
    checksum: 'ghi789',
    mimeType: 'video/mp4',
  },
  {
    id: 'media-4',
    type: 'video',
    name: 'For Bigger Escapes',
    url: 'https://storage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4',
    duration: 15,
    fileSize: 2299653,
    checksum: 'jkl012',
    mimeType: 'video/mp4',
  },
  {
    id: 'media-5',
    type: 'video',
    name: 'For Bigger Fun',
    url: 'https://storage.googleapis.com/gtv-videos-bucket/sample/ForBiggerFun.mp4',
    duration: 60,
    fileSize: 5765645,
    checksum: 'mno345',
    mimeType: 'video/mp4',
  },
];

const weekendPlaylist: PlaylistItem[] = [
  {
    id: 'media-6',
    type: 'video',
    name: 'Sintel',
    url: 'https://storage.googleapis.com/gtv-videos-bucket/sample/Sintel.mp4',
    duration: 888,
    fileSize: 190612452,
    checksum: 'pqr678',
    mimeType: 'video/mp4',
  },
  {
    id: 'media-7',
    type: 'video',
    name: 'Tears of Steel',
    url: 'https://storage.googleapis.com/gtv-videos-bucket/sample/TearsOfSteel.mp4',
    duration: 734,
    fileSize: 149952032,
    checksum: 'stu901',
    mimeType: 'video/mp4',
  },
];

const fallbackPlaylistItems: PlaylistItem[] = [
  {
    id: 'media-fallback-1',
    type: 'video',
    name: 'For Bigger Joyrides',
    url: 'https://storage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4',
    duration: 15,
    fileSize: 2299653,
    checksum: 'vwx234',
    mimeType: 'video/mp4',
  },
];

// =============================================================================
// MOCK CAMPAIGNS
// =============================================================================

const now = new Date();
const oneYearFromNow = new Date(now);
oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);

const oneMonthFromNow = new Date(now);
oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);

export const mockCampaigns: Campaign[] = [
  // Campaign 1: Morning promotions (weekdays 06:00-12:00)
  {
    id: 'campaign-morning',
    name: 'Morning Promotions',
    description: 'Breakfast and morning deals',
    startDate: '2024-01-01',
    endDate: oneYearFromNow.toISOString().split('T')[0],
    priority: 10,
    status: 'active',
    schedule: [
      {
        id: 'sched-morning',
        daysOfWeek: [1, 2, 3, 4, 5], // Mon-Fri
        startTime: '06:00',
        endTime: '12:00',
        timezone: 'America/Sao_Paulo',
      },
    ],
    playlist: morningPlaylist,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },

  // Campaign 2: Afternoon promotions (weekdays 12:00-18:00)
  {
    id: 'campaign-afternoon',
    name: 'Afternoon Promotions',
    description: 'Lunch specials and afternoon deals',
    startDate: '2024-01-01',
    endDate: oneYearFromNow.toISOString().split('T')[0],
    priority: 10,
    status: 'active',
    schedule: [
      {
        id: 'sched-afternoon',
        daysOfWeek: [1, 2, 3, 4, 5], // Mon-Fri
        startTime: '12:00',
        endTime: '18:00',
        timezone: 'America/Sao_Paulo',
      },
    ],
    playlist: afternoonPlaylist,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },

  // Campaign 3: Weekend specials (Sat-Sun all day)
  {
    id: 'campaign-weekend',
    name: 'Weekend Specials',
    description: 'Weekend-only promotions',
    startDate: '2024-01-01',
    endDate: oneYearFromNow.toISOString().split('T')[0],
    priority: 15, // Higher priority than weekday campaigns
    status: 'active',
    schedule: [
      {
        id: 'sched-weekend',
        daysOfWeek: [0, 6], // Sat, Sun
        startTime: '00:00',
        endTime: '23:59',
        timezone: 'America/Sao_Paulo',
      },
    ],
    playlist: weekendPlaylist,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },

  // Campaign 4: Always active (for easy testing)
  {
    id: 'campaign-always',
    name: 'Always Active (Test)',
    description: 'Campaign always active for testing purposes',
    startDate: '2024-01-01',
    endDate: oneYearFromNow.toISOString().split('T')[0],
    priority: 1, // Low priority, other campaigns override
    status: 'active',
    schedule: [], // Empty schedule = always active during date range
    playlist: morningPlaylist,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },

  // Campaign 5: Future campaign (not yet active)
  {
    id: 'campaign-future',
    name: 'Future Promo',
    description: 'This campaign starts next month',
    startDate: oneMonthFromNow.toISOString().split('T')[0],
    endDate: oneYearFromNow.toISOString().split('T')[0],
    priority: 100, // Very high priority
    status: 'active',
    schedule: [],
    playlist: weekendPlaylist,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
];

// =============================================================================
// FALLBACK PLAYLIST
// =============================================================================

export const fallbackPlaylist: PlaylistItem[] = fallbackPlaylistItems;

// =============================================================================
// FETCH FUNCTION (simulates API call)
// =============================================================================

export async function fetchCampaigns(): Promise<Campaign[]> {
  // Simulate network latency
  await new Promise((resolve) => setTimeout(resolve, 500));
  return mockCampaigns;
}

export async function fetchFallbackPlaylist(): Promise<PlaylistItem[]> {
  await new Promise((resolve) => setTimeout(resolve, 200));
  return fallbackPlaylist;
}
